import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
function Economics36() {

       const contentRef = useRef(null);
         const links = {
        prev: "/economics-35", 
        index: "/index-economics",                 
        next: "/economics-37", 
      };
      const titleText = "How The Price System Guides Economic Efficiency";

      const questions = [
  {
    text: "1. What primarily determines the price of a commodity in a free market economy?",
    options: [
      { value: "A", label: "A. Government regulations" },
      { value: "B", label: "B. Cost of manufacturing" },
      {
        value: "C",
        label: "C. Relationship between supply and demand",
      },
      { value: "D", label: "D. Number of workers in the industry" },
    ],
    correct: "C",
  },
  {
    text: "2. What happens when a product becomes highly profitable in the market?",
    options: [
      {
        value: "A",
        label: "A. Its production is taken over by the government",
      },
      { value: "B", label: "B. Prices fall permanently" },
      {
        value: "C",
        label:
          "C. More manufacturers enter the market, increasing competition",
      },
      { value: "D", label: "D. Demand for it decreases immediately" },
    ],
    correct: "C",
  },
  {
    text: "3. According to today’s lesson, what is a false assumption about price determination?",
    options: [
      {
        value: "A",
        label: "A. Prices are determined by supply and demand",
      },
      { value: "B", label: "B. Profitability attracts competition" },
      {
        value: "C",
        label: "C. Cost of production dictates the price",
      },
      {
        value: "D",
        label: "D. Inefficient manufacturers exit the market",
      },
    ],
    correct: "C",
  },
  {
    text: "4. Why is it unrealistic to expect manufacturers to keep producing unprofitable goods?",
    options: [
      {
        value: "A",
        label: "A. They lack interest in their workers",
      },
      {
        value: "B",
        label:
          "B. Manufacturing is controlled entirely by consumer behavior",
      },
      {
        value: "C",
        label:
          "C. Resources are limited and must be allocated efficiently",
      },
      {
        value: "D",
        label: "D. They do not understand consumer needs",
      },
    ],
    correct: "C",
  },
];

      
        // State to track selected answers for all questions
            const [answers, setAnswers] = useState({});
          
            // Function to update an answer
            const handleAnswerChange = (questionIndex, selectedValue) => {
              setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
            };
          
            // Compute score
            const score = questions.reduce((total, q, index) => {
              return total + (answers[index] === q.correct ? 1 : 0);
            }, 0);
          
      

  return (
    <div className="container">
       <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={36}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 36</b>! Today’s lesson unpacks one of the most
        foundational concepts in economics: the price system. While prices may
        appear to be simple numbers attached to goods, they are in fact signals
        that govern the allocation of resources, the survival of industries, and
        the broader direction of an economy. By exploring how supply and demand
        influence prices and how those prices in turn affect future production
        decisions, we’ll discover why the price mechanism is superior to any
        centralized planning attempt.
        <br />
        <br />
        <b>The Self-Regulating Nature of Prices</b>
        <br />
        <br />
        In a functioning free market economy, prices are not arbitrary. They are
        the result of a dynamic relationship between supply and demand. When a
        particular good is in demand, its price increases. This price rise
        attracts producers who hope to capitalize on higher profits. As more
        manufacturers enter the market, competition increases, which eventually
        brings profit margins down. Prices begin to stabilize, often dropping,
        and only the most efficient producers can continue to operate
        sustainably. Less efficient manufacturers, unable to compete at lower
        margins, are forced to exit the market. This is not an unfortunate
        byproduct of capitalism; rather, it is the very process by which markets
        regulate themselves. The oversupply is corrected as unprofitable players
        exit, which reduces overall supply, causing prices to rise again,
        creating a new equilibrium. This cycle continues organically without any
        central intervention. The false belief that prices are determined by the
        cost of manufacturing overlooks the consumer’s role. No matter how
        expensive a product is to manufacture, if there is no corresponding
        demand, prices cannot rise just to accommodate costs. Consumers are not
        obligated to reward inefficiency. Thus, cost does not dictate price.
        Demand does. This understanding of prices is fundamental to economic
        coordination and debunks the simplistic view that high costs alone
        justify higher prices.
        <br />
        This self-regulating nature of prices also acts as a form of discipline.
        It punishes wastefulness and rewards innovation. In industries where
        costs are reduced through better technology or processes, prices can
        come down while profits remain healthy. This incentivizes businesses to
        keep improving, not just to survive but to thrive. Markets operate in a
        continuous feedback loop. Rising prices encourage production; oversupply
        brings them down. Falling prices discourage further production,
        eventually stabilizing the market. It is a movement of millions of
        individual decisions guided by nothing more than the pursuit of value
        and the balancing act between what is supplied and what is demanded.
        This elegant mechanism is what economists refer to as spontaneous order.
        It requires no central coordinator. It works because people are
        constantly making choices based on the information prices provide. This
        alone makes the price system more efficient than any form of imposed
        control.
        <br />
        <br />
        <b>The Moral Confusion Behind Anti-Profit Thinking</b>
        <br />
        <br />
        One of the most persistent misconceptions about economics arises from a
        moral or emotional misunderstanding of profit. Critics of the free
        market price system often argue that manufacturers should not reduce
        production merely because profits decline. They insist that products
        should be created solely for their utility, not for gain. This thinking
        confuses the goals of an individual enterprise with the goals of
        society. It may sound compassionate to suggest that production should
        continue for the sake of human needs, but in practice, such thinking
        leads to economic disarray. If businesses continue producing
        unprofitable goods, they divert valuable capital and labor from areas
        where they are more needed. This belief stems from examining only the
        short-term impact on a specific group, such as workers in a declining
        industry, while ignoring the wider consequences for the economy.
        <br />
        Profit is not a selfish motive. It is a signal. When an industry becomes
        less profitable, it is the market’s way of telling us that consumer
        preferences have shifted. Responding to these shifts is not
        exploitation. It is adaptation. Sustaining an unprofitable industry for
        the sake of appearances or emotion only results in misallocation of
        resources and economic stagnation. For example, if a certain type of
        farming becomes unsustainable due to changing tastes or better
        alternatives, insisting that it be maintained through subsidies or
        mandates does not help society. It merely locks capital and labor in
        less productive avenues. These resources could have been redirected
        toward industries where consumer demand is growing and where the
        potential for prosperity is higher.
        <br />
        Moreover, condemning profit as immoral often overlooks the very real
        benefits it brings. It is profit that drives entrepreneurs to innovate,
        to serve customers better, to reduce costs, and to create employment.
        Without the prospect of profit, there would be little reason for
        risk-taking, little incentive for reinvestment, and no motive for
        continuous improvement. Far from being a sign of greed, profit is a
        reflection of value creation. When a business earns a profit, it is
        because it has delivered goods or services that people voluntarily paid
        for. This voluntary exchange is the hallmark of a healthy economy. The
        moral confusion arises when observers focus only on outcomes without
        considering the processes that led to them. Yes, some people lose jobs
        in unprofitable sectors, but they also gain new opportunities elsewhere.
        The economy is not a zero-sum game. It evolves, and so must its
        participants.
        <br />
        <br />
        <b>The Superiority of Decentralized Decision Making</b>
        <br />
        <br />
        Many who critique the price mechanism believe that governments or
        committees of experts should decide which industries deserve support and
        which should shrink. However, what they fail to realize is that such
        central planning lacks the information and responsiveness that the
        market naturally possesses. An economy in equilibrium is one in which
        every expansion comes at the expense of another. If capital and labor
        move out of one sector, they are not vanishing. They are being absorbed
        into another that is more aligned with consumer demand. This
        reallocation happens not through commands from a bureaucratic agency but
        through the silent, efficient messaging of prices.
        <br />
        No central planner, no matter how intelligent, can gather and process
        the enormous, constantly changing data embedded in every price point.
        The brilliance of the price system lies in its decentralization. It
        harnesses the knowledge of millions of individuals without needing them
        to communicate directly. Every transaction, every production decision,
        and every shift in consumer behavior is reflected in prices, making them
        the most democratic and effective tool for organizing economic activity.
        Attempting to replace this spontaneous order with planned directives is
        not only inefficient but also harmful. Bureaucrats cannot match the
        market’s speed, precision, or adaptability. The market’s invisible hand,
        driven by supply, demand, and price signals, solves complex economic
        problems daily. These are problems that even the most well-meaning
        planning board could not address without unintended consequences.
        <br />
        Furthermore, the decentralization of decision making allows for
        experimentation. Different firms try different approaches. Some succeed,
        others fail. The ones that succeed set new benchmarks for others. This
        process of trial and error fuels progress. It cannot be replicated in an
        environment where all decisions are handed down from above. Central
        planning stifles initiative. It rewards conformity. It resists change.
        In contrast, a decentralized economy encourages innovation, risk taking,
        and responsiveness. It is not perfect, but it is superior. The evidence
        of history confirms this. Economies that embraced the price system have
        outperformed those that rejected it. They have produced more,
        distributed better, and evolved faster. This is not a coincidence. It is
        the natural result of allowing individuals to respond to the needs of
        others through the language of prices.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        In today's lesson, we examined the powerful role that supply and demand
        play in determining the prices of goods and services. Contrary to the
        misguided belief that prices are dictated by the cost of production, we
        learned that market dynamics are the real drivers of price adjustments.
        When a commodity becomes profitable, it attracts more manufacturers,
        increasing competition, lowering profit margins, and eventually pushing
        out less efficient producers. This natural cycle ensures that only the
        most efficient players remain, maintaining balance and efficiency in the
        market. We also addressed the mistaken notion that industries should
        keep producing for the sake of utility, regardless of profitability.
        Instead, we emphasized the importance of recognizing that in a free
        economy, resources like labor and capital are limited, and must be
        directed toward the most profitable and sustainable ventures. The price
        system plays a crucial role in this process, allocating resources in a
        manner far superior to what central planning or bureaucratic
        decision-making could achieve.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In the next lesson, we will examine scarcity in its purest economic form and explore why it makes choice unavoidable. You will learn how limited resources force individuals and societies to prioritize, trade off, and economize, and why every economic problem ultimately traces back to scarcity. This lesson will prepare you to understand why concepts like value, cost, property, and production logically emerge only in a world where resources are finite and human wants are unlimited.<br/><br/>
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics36;
